/**
 * Pass Builder layout tokens.
 *
 * Chakra spacing props (`spacing`, `mb`, `gap`, etc.) only map to the theme
 * scale for defined keys (e.g. 4 → 1rem, 16 → 4rem). Any other number is
 * treated as a literal pixel value — `mb={7}` is 7px, not 1.75rem.
 * Use a scale key, or an explicit string such as `"29px"`, for precise sizes.
 *
 * @see https://chakra-ui.com/docs/styled-system/theme#spacing
 */
export const PASS_BUILDER_FIELD_VARIANT = "outline" as const

/** Sketch: width of Label / Value text inputs and dropdowns */
export const PASS_BUILDER_FIELD_WIDTH = "356px"

/** Sketch: width of each auxiliary field column input */
export const PASS_BUILDER_AUXILIARY_FIELD_WIDTH = "212px"

/** Sketch: horizontal gap between auxiliary columns */
export const PASS_BUILDER_AUXILIARY_COL_GAP = "30px"

/** Sketch: Additional Information back-section row */
export const PASS_BUILDER_BACK_TYPE_WIDTH = "168px"
export const PASS_BUILDER_BACK_LABEL_WIDTH = "147px"
export const PASS_BUILDER_BACK_VALUE_WIDTH = "463px"
export const PASS_BUILDER_BACK_COL_GAP = "10px"
export const PASS_BUILDER_BACK_DELETE_COL_WIDTH = "24px"

/** Gap between front-of-pass field blocks — theme key 16 (= 4rem / 64px) */
export const SUBSECTION_GAP = 16

/** Space below description before field controls */
export const FIELD_BLOCK_DESCRIPTION_MB = 4

/** Vertical gap between Label / Value / static rows */
export const FIELD_CONTROL_STACK_SPACING = 4

/** Sketch Pass Builder: icon → platform label */
export const PLATFORM_ICON_LABEL_GAP = "5px"

/** Sketch: end of Apple group → Google icon */
export const PLATFORM_PAIR_GAP = "13px"

/** Sketch: end of platform group → Required / Optional */
export const PLATFORM_STATUS_GAP = "20px"

export const PLATFORM_SPLIT_GAP = "36px"

/** Sketch Pass Builder upload placeholder */
export const DROP_ZONE_BORDER = "#979797"
export const DROP_ZONE_BG = "#F6F6F6"

/** Theme key 12 (= 3rem / 48px) below Branding section title */
export const SECTION_TITLE_MB_PRIMARY = 12

/** Sketch ~29px below other section titles (not a theme key — use px) */
export const SECTION_TITLE_MB = "29px"

/** Theme key 10 (= 2.5rem / 40px) below Colors section title */
export const SECTION_TITLE_MB_COLORS = 10

/** Theme key 16 (= 4rem / 64px) */
export const SECTION_ABOVE_DIVIDER_SPACING = 16
export const SECTION_BELOW_DIVIDER_SPACING = 16

export const SECTION_DIVIDER_COLOR = "gray.400"

/** Sketch: preview sidebar platform switcher (Rectangle 5) */
export const PASS_BUILDER_PREVIEW_TAB_WIDTH = "290px"
export const PASS_BUILDER_PREVIEW_TAB_HEIGHT = "88px"
export const PASS_BUILDER_PREVIEW_TAB_PADDING = "4px"
export const PASS_BUILDER_PREVIEW_TAB_GAP = "2px"
export const PASS_BUILDER_PREVIEW_TAB_SEGMENT_WIDTH = "140px"
export const PASS_BUILDER_PREVIEW_TAB_SEGMENT_HEIGHT = "80px"
/** Inset of sliding thumb within each segment */
export const PASS_BUILDER_PREVIEW_TAB_THUMB_INSET = "2px"
export const PASS_BUILDER_PREVIEW_TAB_THUMB_WIDTH = "136px"
export const PASS_BUILDER_PREVIEW_TAB_THUMB_HEIGHT = "76px"
/** Vertical gap between wallet icon and label in preview tab segments (Sketch ~5px) */
export const PASS_BUILDER_PREVIEW_TAB_ICON_LABEL_GAP = "5px"

/** Sketch Apple Passbook in preview sidebar (scaled from 320px base artboard) */
export const PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH = 320
export const PASS_BUILDER_PREVIEW_PASS_BASE_HEIGHT = 469
export const PASS_BUILDER_PREVIEW_PASS_SCALE_WIDTH = 377
export const PASS_BUILDER_PREVIEW_PASS_SCALE = (
  PASS_BUILDER_PREVIEW_PASS_SCALE_WIDTH / PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH
)

/** Sketch `Passbook Back - Custom` card height (375×550), scaled to 320px base width. */
export const PASS_BUILDER_PREVIEW_BACK_BASE_HEIGHT = Math.round(
  550 * (PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH / 375),
)

/** Sketch back preview: mini front strip (150×93 on 375px-wide card) via CSS scale.
 *  Height runs a little past Sketch's 93 so the crop clears the auxiliary row. */
const PASS_BUILDER_PREVIEW_BACK_CARD_WIDTH_REF = 375
export const PASS_BUILDER_PREVIEW_BACK_THUMB_WIDTH_REF = 113
export const PASS_BUILDER_PREVIEW_BACK_THUMB_HEIGHT_REF = 84
export const PASS_BUILDER_PREVIEW_BACK_THUMB_SCALE = (
  PASS_BUILDER_PREVIEW_BACK_THUMB_WIDTH_REF / PASS_BUILDER_PREVIEW_BACK_CARD_WIDTH_REF
)
export const PASS_BUILDER_PREVIEW_BACK_THUMB_WIDTH = Math.round(
  PASS_BUILDER_PREVIEW_BACK_THUMB_WIDTH_REF
    * (PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH / PASS_BUILDER_PREVIEW_BACK_CARD_WIDTH_REF),
)
export const PASS_BUILDER_PREVIEW_BACK_THUMB_HEIGHT = Math.round(
  PASS_BUILDER_PREVIEW_BACK_THUMB_HEIGHT_REF
    * (PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH / PASS_BUILDER_PREVIEW_BACK_CARD_WIDTH_REF),
)

/** Sketch `Passbook Back - Custom` white inset panels (345px wide on 375px card). */
export const PASS_BUILDER_PREVIEW_BACK_PANEL_WIDTH_REF = 345
export const PASS_BUILDER_PREVIEW_BACK_PANEL_INSET_REF = 16
export const PASS_BUILDER_PREVIEW_BACK_PANEL_RADIUS_REF = 20
export const PASS_BUILDER_PREVIEW_BACK_PANEL_GAP_REF = 30
/** Gap between the pass title and the first section panel. */
export const PASS_BUILDER_PREVIEW_BACK_TITLE_TO_PANEL_REF = 44
export const PASS_BUILDER_PREVIEW_BACK_PANEL_PADDING_REF = 14

/** Sketch `Passbook Back - Custom` typography & colors */
// iOS renders the back of a pass over its grouped-table background, which is
// what makes the section panels read as cards rather than plain rows.
export const PASS_BUILDER_PREVIEW_BACK_COLOR_PAGE = "#F2F2F7"
export const PASS_BUILDER_PREVIEW_BACK_COLOR_TEXT = "#1E1E1E"
export const PASS_BUILDER_PREVIEW_BACK_COLOR_LINK = "#007AFF"
export const PASS_BUILDER_PREVIEW_BACK_COLOR_BODY = "#959393"
export const PASS_BUILDER_PREVIEW_BACK_COLOR_DIVIDER = "#D8D8DC"

export const PASS_BUILDER_PREVIEW_BACK_FONT_TITLE_REF = 27
export const PASS_BUILDER_PREVIEW_BACK_FONT_ROW_REF = 16
export const PASS_BUILDER_PREVIEW_BACK_FONT_BODY_REF = 14

/** Sketch vertical rhythm (375×550 card) */
export const PASS_BUILDER_PREVIEW_BACK_THUMB_TOP_REF = 64
export const PASS_BUILDER_PREVIEW_BACK_TITLE_AFTER_THUMB_REF = 22
export const PASS_BUILDER_PREVIEW_BACK_TITLE_LINE_HEIGHT_REF = 56
export const PASS_BUILDER_PREVIEW_BACK_INFO_PANEL_PT_REF = 0
export const PASS_BUILDER_PREVIEW_BACK_INFO_PANEL_PB_REF = 0
export const PASS_BUILDER_PREVIEW_BACK_INFO_GAP_BEFORE_DIVIDER_REF = 10
export const PASS_BUILDER_PREVIEW_BACK_INFO_GAP_AFTER_DIVIDER_REF = 10
export const PASS_BUILDER_PREVIEW_BACK_INFO_LABEL_BODY_GAP_REF = 15

/** Sketch: preview sidebar vertical spacing */
export const PASS_BUILDER_PREVIEW_SIDEBAR_TOP_PADDING = "32px"
/** Tab switcher bottom → section title (61px → 105px in Sketch) */
export const PASS_BUILDER_PREVIEW_TAB_TO_SECTION_GAP = "44px"
/** Section title → pass card below it */
export const PASS_BUILDER_PREVIEW_SECTION_TITLE_MB = "20px"
/** Front pass bottom → back section title (~45px in Sketch at 377px pass width) */
export const PASS_BUILDER_PREVIEW_PASS_TO_BACK_GAP = "45px"
export const PASS_BUILDER_PREVIEW_SCROLL_PADDING_BOTTOM = "32px"

/** Sketch: "Front/Back of the Pass Preview" labels (16px, 24px line-height) */
export const PASS_BUILDER_PREVIEW_SECTION_TITLE_FONT_SIZE = "16px"
export const PASS_BUILDER_PREVIEW_SECTION_TITLE_LINE_HEIGHT = "24px"
export const PASS_BUILDER_PREVIEW_SECTION_TITLE_LETTER_SPACING = "0.04em"
